import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { config } from "@/config";
import { SettingsApi } from "@/api/giftcard.api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/ui/data-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  Plus,
  Eye,
  MoreHorizontal,
  Trash2,
  RotateCcw,
  CreditCard,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { PaginationControls } from "@/components/common/PaginationControl";
import { cn } from "@/lib/utils";

/** Placeholder until issued card numbers exist */
const MASKED_CARD_NUMBER_DISPLAY = "•••• •••• •••• ••••";

function formatExpiryMmYy(expiry: string | undefined): string {
  if (!expiry || expiry.startsWith("0001-01-01")) return "—";
  try {
    return format(new Date(expiry), "MM/yy");
  } catch {
    return "—";
  }
}

function GiftCardCreditPreview({ card }: { card: Template }) {
  const currency = localStorage.getItem("giftCardCurrency") || "USD";
  const imgSrc = card.bannerImagePath || "/placeholder.svg";
  const statusLabel = card.isActive ? "Active" : "Inactive";
  const redemption =
    card.isPartial === true
      ? "Partial"
      : card.isPartial === false
        ? "Full"
        : "—";

  return (
    <div
      className={cn(
        "relative mx-auto w-full max-w-[420px] overflow-hidden rounded-2xl border border-slate-200/90",
        "shadow-[0_24px_48px_rgba(0,0,0,0.18)]",
        "aspect-[1.586/1]"
      )}
    >
      <img
        src={imgSrc}
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
        onError={(e) => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = "/placeholder.svg";
        }}
      />
      {/* Light frosted layer: keeps banner visible but makes black text readable on any image */}
      <div
        className="absolute inset-0 bg-white/80 backdrop-blur-[1px]"
        aria-hidden
      />
      <div className="relative z-10 flex h-full flex-col justify-between p-6 text-slate-900 sm:p-7">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <CreditCard
              className="h-6 w-6 shrink-0 text-amber-800"
              aria-hidden
            />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600">
                Gift OS
              </p>
              <p className="text-xs font-medium text-black">Digital Gift</p>
            </div>
          </div>
          <span
            className={cn(
              "rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
              card.isActive
                ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                : "border-slate-300 bg-slate-100 text-slate-800"
            )}
          >
            {statusLabel}
          </span>
        </div>

        <div className="space-y-5">
          <p className="font-mono text-lg tracking-[0.12em] text-black sm:text-xl [text-shadow:0_1px_0_rgba(255,255,255,0.85)]">
            {MASKED_CARD_NUMBER_DISPLAY}
          </p>

          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
            <div className="min-w-0 sm:col-span-2">
              <p className="text-[10px] font-medium uppercase tracking-wider text-slate-600">
                Card name
              </p>
              <p className="truncate font-semibold leading-tight text-black">
                {card.title || "Gift card"}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-medium uppercase tracking-wider text-slate-600">
                Valid thru
              </p>
              <p className="font-mono font-semibold tabular-nums text-black">
                {formatExpiryMmYy(card.expiry)}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-medium uppercase tracking-wider text-slate-600">
                Redeem
              </p>
              <p className="font-semibold capitalize text-black">{redemption}</p>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-[10px] font-medium uppercase tracking-wider text-slate-600">
                Value
              </p>
              <p className="text-lg font-bold tabular-nums leading-tight text-black sm:text-xl">
                {card.amount.toFixed(2)}
                <span className="ml-1 text-sm font-semibold text-slate-800">
                  {currency}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

type Template = {
  id: number;
  bannerImage: string;
  bannerImagePath: string;
  title: string;
  description: string;
  amount: number;
  createdDateTime: string;
  isActive: boolean;
  expiry: string;
  isPartial?: boolean;
};

const CreateGiftCardList: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [data, setData] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<Template | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState<Template | null>(null);
  const [cardImagePreview, setCardImagePreview] = useState<Template | null>(
    null
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalGiftCards, setTotalGiftCards] = useState(0);
  const [malls, setMalls] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedMallId, setSelectedMallId] = useState<number | null>(null);
  const [isMallLoading, setIsMallLoading] = useState(false);
  const hasInitialFetchRef = useRef(false);

  // Define fetchGiftCards first so it can be called from fetchMallOptions
  const fetchGiftCards = async (options?: {
    page?: number;
    pageSize?: number;
    mallId?: number | null;
  }) => {
    setIsLoading(true);
    try {
      // Get userId from sessionStorage
      const userInfo = sessionStorage.getItem("userInfo");
      const userId = userInfo ? JSON.parse(userInfo).userId : 1;

      // Resolve pagination and mall values
      const currentPage = options?.page ?? page;
      const currentPageSize = options?.pageSize ?? rowsPerPage;
      const mallIdToUse =
        options?.mallId !== undefined ? options.mallId : selectedMallId ?? 0;

      const response = await axios.post(
        `${config.GIFT_API_BASE_URL}/GiftCard/GetAllGiftCard`,
        {
          p_Id: userId,
          p_MallId: mallIdToUse,
          // Backend expects 1-based page index
          p_Page: currentPage + 1,
          p_PageSize: currentPageSize,
        },
        {
          headers: {
            XApiKey: config.GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.giftCards) {
        const list = response.data.giftCards;
        setData(list);
        setTotalGiftCards(response.data.totalCount ?? list.length ?? 0);
      }
    } catch (error) {
      console.error("Error fetching gift cards:", error);
      toast.error("Failed to load gift cards. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMallOptions = useCallback(async () => {
    setIsMallLoading(true);
    try {
      const data = await SettingsApi.getMallDetails();
      const mallsData = Array.isArray(data?.malldetails)
        ? data.malldetails
        : [];
      const mapped = mallsData.map((mall: any) => ({
        id: mall.mallId,
        name: mall.mallName || `Mall ${mall.mallId}`,
      }));
      setMalls(mapped);

      // Pre-select the first mall immediately after malls are loaded
      if (mapped.length > 0) {
        const firstMallId = mapped[0].id;
        setSelectedMallId(firstMallId);
        // Call fetchGiftCards directly with the first mall ID and initial pagination
        hasInitialFetchRef.current = true;
        fetchGiftCards({
          page: 0,
          pageSize: rowsPerPage,
          mallId: firstMallId,
        });
      }
    } catch (error) {
      console.error("Error fetching malls:", error);
      setMalls([]);
      toast.error("Failed to load mall list");
      // If malls fail to load, only then use mallId 0
      setSelectedMallId(null);
      hasInitialFetchRef.current = true;
      fetchGiftCards({
        page: 0,
        pageSize: rowsPerPage,
        mallId: 0,
      });
    } finally {
      setIsMallLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMallOptions();
  }, [fetchMallOptions]);

  // Server-side pagination - use backend totalCount
  const totalPages = Math.max(
    1,
    Math.ceil((totalGiftCards || 0) / (rowsPerPage || 1))
  );

  // Centralized handler for page changes (0-based index)
  const handlePageChange = (newPage: number) => {
    if (newPage < 0 || newPage > totalPages - 1) return;
    setPage(newPage);
    fetchGiftCards({
      page: newPage,
      pageSize: rowsPerPage,
      mallId: selectedMallId,
    });
  };

  const handleViewDetails = (card: Template) => {
    setSelectedCard(card);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (card: Template) => {
    setCardToDelete(card);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!cardToDelete) return;

    try {
      // Get user ID from session storage
      const userInfo = sessionStorage.getItem("userInfo");
      if (!userInfo) {
        toast.error("User information not found");
        return;
      }

      const { userId } = JSON.parse(userInfo);

      // Call delete API
      const response = await axios.post(
        `${config.GIFT_API_BASE_URL}/GiftCard/DeleteGiftCard`,
        {
          id: cardToDelete.id,
          action: false,
          updatedBy: userId,
        },
        {
          headers: {
            xapikey: config.GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      // Check for error_msg in response
      const errorMsg = response.data?.error_msg;
      if (errorMsg && errorMsg.trim() !== "") {
        // Show error message, close dialog, but keep card in table (stay in same position)
        toast.error(errorMsg);
        setIsDeleteDialogOpen(false);
        setCardToDelete(null);
        return;
      }

      // No error, proceed with deletion
      // Remove from local state
      setData((prevData) =>
        prevData.filter((card) => card.id !== cardToDelete.id)
      );

      // Refresh the gift card list
      await fetchGiftCards({
        page,
        pageSize: rowsPerPage,
        mallId: selectedMallId,
      });

      toast.success("Gift card deleted successfully");
      setIsDeleteDialogOpen(false);
      setCardToDelete(null);
    } catch (error) {
      console.error("Error deleting gift card:", error);
      toast.error("Failed to delete gift card");
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    setCardToDelete(null);
  };

  // Reset filters
  const handleReset = () => {
    setSearch("");
    setPage(0);
    // Reset to first mall option (same as initial load)
    if (malls.length > 0) {
      const firstMallId = malls[0].id;
      setSelectedMallId(firstMallId);
      // Fetch gift cards with the first mall and first page
      fetchGiftCards({
        page: 0,
        pageSize: rowsPerPage,
        mallId: firstMallId,
      });
    } else {
      setSelectedMallId(null);
    }
  };

  const columns = [
    {
      header: "Image",
      accessorKey: "bannerImagePath" as keyof Template,
      cell: (row: Template) => (
        <button
          type="button"
          onClick={() => setCardImagePreview(row)}
          className="group relative inline-flex rounded-md ring-offset-2 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
          title="Preview as card"
          aria-label={`Preview gift card image for ${row.title}`}
        >
          <img
            src={row.bannerImagePath || "/placeholder.svg"}
            alt=""
            className="h-10 w-16 object-cover rounded border border-gray-200 transition group-hover:opacity-90 group-hover:ring-2 group-hover:ring-amber-400/60"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/placeholder.svg";
            }}
          />
        </button>
      ),
    },
    {
      header: "Title",
      accessorKey: "title" as keyof Template,
      cell: (row: Template) => <span className="font-medium">{row.title}</span>,
    },
    {
      header: "Description",
      accessorKey: "description" as keyof Template,
      cell: (row: Template) => (
        <span
          className="text-sm text-gray-600 line-clamp-2 max-w-md"
          title={row.description}
        >
          {row.description}
        </span>
      ),
    },
    {
      header: "Amount",
      accessorKey: "amount" as keyof Template,
      cell: (row: Template) => {
        const currency = localStorage.getItem("giftCardCurrency") || "USD";
        return (
          <span className="font-medium">
            {row.amount.toFixed(2)} {currency}
          </span>
        );
      },
    },
    {
      header: "Expiry",
      accessorKey: "expiry" as keyof Template,
      cell: (row: Template) => {
        if (!row.expiry || row.expiry.startsWith("0001-01-01")) {
          return <span className="text-sm text-gray-500">No expiry</span>;
        }
        try {
          return (
            <span className="text-sm">
              {format(new Date(row.expiry), "MMM dd, yyyy")}
            </span>
          );
        } catch {
          return <span className="text-sm text-gray-500">Invalid date</span>;
        }
      },
    },
    {
      header: "Created",
      accessorKey: "createdDateTime" as keyof Template,
      cell: (row: Template) => (
        <span className="text-sm">
          {format(new Date(row.createdDateTime), "MMM dd, yyyy")}
        </span>
      ),
    },
    {
      header: "Actions",
      id: "actions",
      cell: (row: Template) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleViewDetails(row)}>
              <Eye className="h-4 w-4 mr-2" />
              View
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleDeleteClick(row)}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      {isLoading ? (
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-48 bg-gray-200 animate-pulse rounded" />
            <div className="h-4 w-64 bg-gray-200 animate-pulse rounded" />
          </div>
          <div className="h-10 w-40 bg-gray-200 animate-pulse rounded" />
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">All Cards</h1>
            <p className="text-gray-600 mt-1">Manage and view all gift cards</p>
          </div>
          <div className="flex items-center gap-4">
            {/* Mall Dropdown */}
            <div className="w-48">
              <Select
                value={
                  selectedMallId !== null
                    ? selectedMallId.toString()
                    : undefined
                }
                onValueChange={(value) => {
                  if (value === "clear") {
                    setSelectedMallId(null);
                    // If initial fetch is done and user is manually changing the mall, fetch immediately
                    if (hasInitialFetchRef.current) {
                      setPage(0);
                      fetchGiftCards({
                        page: 0,
                        pageSize: rowsPerPage,
                        mallId: null,
                      });
                    }
                  } else {
                    const newMallId = Number(value);
                    setSelectedMallId(newMallId);
                    // If initial fetch is done and user is manually changing the mall, fetch immediately
                    if (hasInitialFetchRef.current) {
                      setPage(0);
                      fetchGiftCards({
                        page: 0,
                        pageSize: rowsPerPage,
                        mallId: newMallId,
                      });
                    }
                  }
                }}
                disabled={isMallLoading || malls.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      isMallLoading ? "Loading malls..." : "Select mall"
                    }
                  />
                </SelectTrigger>
                <SelectContent className="!max-h-48 overflow-auto py-0">
                  <SelectItem value="clear" className="text-gray-500 italic">
                    Clear selection
                  </SelectItem>
                  {malls.length > 0 ? (
                    malls.map((mall) => (
                      <SelectItem key={mall.id} value={mall.id.toString()}>
                        {mall.name}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      {isMallLoading ? "Loading..." : "No malls found"}
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => navigate("/giftcard/create-new")}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Card
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="h-10 w-full max-w-sm bg-gray-100 animate-pulse rounded" />
          ) : (
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by title"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(0);
                    }}
                    className="pl-9"
                  />
                </div>
              </div>

              {/* Reset Button */}
              <Button variant="outline" onClick={handleReset} className="gap-2">
                <RotateCcw className="h-4 w-4" />
                Reset
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-3 p-6">
              {[1, 2, 3, 4, 5].map((row) => (
                <div
                  key={`template-skeleton-${row}`}
                  className="h-12 w-full bg-gray-100 animate-pulse rounded"
                />
              ))}
            </div>
          ) : (
            <DataTable title="Cards" data={data} columns={columns} />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!isLoading && (
        <div className="mt-4 flex justify-between items-center">
          {/* Rows per page selector */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">Rows per page:</span>
            <Select
              value={rowsPerPage.toString()}
              onValueChange={(value) => {
                const newRowsPerPage = parseInt(value, 10);
                setRowsPerPage(newRowsPerPage);
                setPage(0); // Reset to first page when changing rows per page
                fetchGiftCards({
                  page: 0,
                  pageSize: newRowsPerPage,
                  mallId: selectedMallId,
                });
              }}
            >
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="30">30</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              onPrevHandler={() => handlePageChange(page - 1)}
              onNextHandler={() => handlePageChange(page + 1)}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      )}

      {/* Credit-card style image preview */}
      <Dialog
        open={cardImagePreview !== null}
        onOpenChange={(open) => {
          if (!open) setCardImagePreview(null);
        }}
      >
        <DialogContent className="max-w-lg border border-slate-200 bg-white p-6 shadow-xl sm:max-w-lg [&>button]:text-slate-600 [&>button]:hover:bg-slate-100 [&>button]:hover:text-slate-900">
          <DialogHeader className="sr-only">
            <DialogTitle>Gift card preview</DialogTitle>
          </DialogHeader>
          {cardImagePreview && (
            <div className="space-y-4">
              <GiftCardCreditPreview card={cardImagePreview} />
              {cardImagePreview.description?.trim() ? (
                <p className="text-center text-xs leading-relaxed text-slate-700">
                  {cardImagePreview.description.length > 160
                    ? `${cardImagePreview.description.slice(0, 160)}…`
                    : cardImagePreview.description}
                </p>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Details Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Card Details</DialogTitle>
          </DialogHeader>

          {selectedCard && (
            <div className="space-y-3">
              {/* Image */}
              <div className="flex justify-center">
                <img
                  src={selectedCard.bannerImagePath || "/placeholder.svg"}
                  alt={selectedCard.title}
                  className="w-full h-40 object-cover rounded-lg border"
                />
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Row 1: ID and Title */}
                <div className="space-y-1 p-3 bg-gray-50 rounded-lg">
                  <h3 className="text-xs font-medium text-gray-500">ID</h3>
                  <p className="font-mono text-sm">{selectedCard.id}</p>
                </div>

                <div className="space-y-1 p-3 bg-gray-50 rounded-lg">
                  <h3 className="text-xs font-medium text-gray-500">Title</h3>
                  <p className="font-medium text-sm">{selectedCard.title}</p>
                </div>

                {/* Row 2: Amount and Status */}
                <div className="space-y-1 p-3 bg-gray-50 rounded-lg">
                  <h3 className="text-xs font-medium text-gray-500">Amount</h3>
                  <p className="font-bold text-green-600">
                    {selectedCard.amount.toFixed(2)}{" "}
                    {localStorage.getItem("giftCardCurrency") || "USD"}
                  </p>
                </div>

                <div className="space-y-1 p-3 bg-gray-50 rounded-lg">
                  <h3 className="text-xs font-medium text-gray-500">Status</h3>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedCard.isActive
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {selectedCard.isActive ? "Active" : "Inactive"}
                  </span>
                </div>

                {/* Row 3: Redemption Type and Expiry */}
                <div className="space-y-1 p-3 bg-gray-50 rounded-lg">
                  <h3 className="text-xs font-medium text-gray-500">
                    Redemption Type
                  </h3>
                  <p className="font-semibold text-sm text-gray-900">
                    {selectedCard.isPartial === true
                      ? "Partial"
                      : selectedCard.isPartial === false
                      ? "Full"
                      : "N/A"}
                  </p>
                </div>

                <div className="space-y-1 p-3 bg-gray-50 rounded-lg">
                  <h3 className="text-xs font-medium text-gray-500">Expiry</h3>
                  {!selectedCard.expiry ||
                  selectedCard.expiry.startsWith("0001-01-01") ? (
                    <p className="text-sm text-gray-500">No expiry</p>
                  ) : (
                    <p className="text-sm">
                      {format(
                        new Date(selectedCard.expiry),
                        "MMM dd, yyyy 'at' HH:mm"
                      )}
                    </p>
                  )}
                </div>

                {/* Row 4: Created (full width) */}
                <div className="space-y-1 p-3 bg-gray-50 rounded-lg md:col-span-2">
                  <h3 className="text-xs font-medium text-gray-500">Created</h3>
                  <p className="text-sm">
                    {format(
                      new Date(selectedCard.createdDateTime),
                      "MMM dd, yyyy 'at' HH:mm"
                    )}
                  </p>
                </div>
              </div>

              {/* Description - Full width at bottom */}
              <div className="space-y-1 p-3 bg-gray-50 rounded-lg">
                <h3 className="text-xs font-medium text-gray-500">
                  Description
                </h3>
                <p className="text-sm text-gray-700">
                  {selectedCard.description}
                </p>
              </div>

              {/* Close Button */}
              <div className="flex justify-center pt-1">
                <Button
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="px-6"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Gift Card</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this gift card? This action cannot
              be undone.
              {cardToDelete && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium">{cardToDelete.title}</p>
                  <p className="text-sm text-gray-600">ID: {cardToDelete.id}</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CreateGiftCardList;
